/**
 * Enroller Admin Service
 * Handles analytics, dashboard, and Metabase-related operations for Enroller Admin
 */

import { apiClient } from '@/services/core/apiClient';

// ─── Strict API response types ────────────────────────────────────────────────

/** Exact shape of GET /dashboard-items/default-iframe response */
export interface DefaultIframeResponse {
  success: boolean;
  itemId?: number;
  userType?: string;
  userEmail?: string;
  assignedBy?: number;
  iframeUrl?: string;
  message?: string;
  expires_at_epoch_ms?: number;
}

/** Exact shape of GET /dashboard-items/{id}/iframe response */
export interface ItemIframeResponse {
  success: boolean;
  iframeUrl?: string;
  expires_at_epoch_ms?: number;
  message?: string;
}

// ─── Legacy types kept for backward compatibility ─────────────────────────────

export interface MetabaseTokenResponse {
  success: boolean;
  id: number;
  type: string;
  token: string;
  iframeUrl?: string;
  expires_at_epoch_ms?: number;
}

export interface MetabaseFilterParam {
  /** Metabase filter slug (used as the query param key) */
  slug: string;
  /** Filter display name (kept for debugging / future use) */
  name?: string;
  valueSource?: 'emailAddress' | 'fullName' | 'userId' | 'static';
  /** Static value when valueSource === 'static' */
  value?: string;
}

export interface MetabaseUserContext {
  userId?: string | number;
  userEmail?: string;
  userFullName?: string;
}

export interface MetabaseConfig {
  type: 'question' | 'dashboard';
  id: number;
  user_email: number | string;
  /** Optional filters to forward as query params (resolved per valueSource) */
  filters?: MetabaseFilterParam[];
  /** Logged-in user context used to resolve dynamic filter values */
  userContext?: MetabaseUserContext;
}

export interface MetabaseResource {
  id: number;
  name: string;
  resource_type: string;
  resource_id: number;
}

export interface MetabaseResourcesResponse {
  resources: MetabaseResource[];
  success: boolean;
  error?: string;
}

export interface MetabaseQuestion {
  id: number;
  name: string;
}

export interface DashboardItem {
  id: number;
  customName: string;
  resourceType: 'dashboard' | 'question';
  resourceId: number;
  metabaseQuestionId?: number;
  metabaseQuestionName?: string;
  userType?: string | null;
  userTypes?: string[] | string | null;
  userRoleIds?: number[] | string | null;
  dashboardQuestions?: string | unknown[] | null;
  dashboardFilters?: string | unknown[] | null;
  isDefault?: boolean;
  createdBy?: number;
  createdAt?: string;
  updatedDate?: string | null;
}

export interface MetabaseResourceFlat {
  resourceType: 'question' | 'dashboard';
  resourceId: number;
  displayName: string;
  compositeKey: string; // "${resourceType}:${resourceId}"
}

const extractArrayPayload = <T>(payload: unknown): T[] => {
  if (Array.isArray(payload)) return payload as T[];

  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const candidates = [record.items, record.dashboardItems, record.resources, record.data];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) return candidate as T[];
    }
  }

  return [];
};

const extractObjectPayload = <T extends object>(payload: unknown): T | null => {
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;

    if ('success' in record || 'iframeUrl' in record || 'token' in record) {
      return payload as T;
    }

    if (record.data && typeof record.data === 'object') {
      return record.data as T;
    }
  }

  return null;
};

const expandStaticFilterValues = (raw: string): string[] => {
  const source = String(raw ?? '').trim();
  if (!source) return [];

  // Support JSON array format: "[\"A\",\"B\"]"
  try {
    const parsed = JSON.parse(source);
    if (Array.isArray(parsed)) {
      return parsed
        .map((v) => String(v).trim())
        .filter((v) => v.length > 0);
    }
  } catch {
    // Not JSON array; continue with text splitting
  }

  // Support URL-encoded comma values and split by common separators.
  const decoded = decodeURIComponent(source);
  return decoded
    .split(/[\n,;]+/)
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
};

export class EnrollerAdminService {
  private userId: string | number | null = null;
  private token: string | null = null;

  updateContext(context: { userId: string | number; token: string; hasuraToken?: string }) {
    this.userId = context.userId;
    this.token = context.token;
  }

  private ensureContext(): void {
    if (!this.userId || !this.token) {
      throw new Error('EnrollerAdminService: User context not set. Call updateContext() first.');
    }
  }

  async getMetabaseResources(): Promise<MetabaseResourcesResponse> {
    this.ensureContext();
    try {
      const endpoint = `/api/ngpep/${this.userId}/metabase-resources`;
      const response = await apiClient.get<MetabaseResource[]>(endpoint, this.token!);
      if (response.success && response.data) {
        const resources = extractArrayPayload<MetabaseResource>(response.data);
        return { resources, success: true };
      }
      return { resources: [], success: false, error: response.error || 'Failed to fetch Metabase resources' };
    } catch (error) {
      console.error('❌ Error fetching Metabase resources:', error);
      return { resources: [], success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  async getMetabaseToken(config: MetabaseConfig): Promise<MetabaseTokenResponse | null> {
    this.ensureContext();
    try {
      const params = new URLSearchParams();
      params.set('type', config.type);
      params.set('id', String(config.id));
      params.set('user_email', '');

      // Resolve and append filter values based on valueSource
      const ctx = config.userContext ?? {};
      for (const f of config.filters ?? []) {
        if (!f?.slug || !f.valueSource) continue;
        let resolved: string | undefined;
        switch (f.valueSource) {
          case 'emailAddress':
            resolved = ctx.userEmail;
            break;
          case 'fullName':
            resolved = ctx.userFullName;
            break;
          case 'userId':
            resolved = ctx.userId !== undefined ? String(ctx.userId) : undefined;
            break;
          case 'static':
            resolved = f.value;
            break;
        }
        if (resolved !== undefined && resolved !== null && String(resolved).length > 0) {
          if (f.valueSource === 'static') {
            // One static input can map to multiple key/value entries in query params,
            // e.g. "A, B, C" -> clinic_name=A&clinic_name=B&clinic_name=C
            const values = expandStaticFilterValues(String(resolved));

            if (values.length > 0) {
              values.forEach((v) => params.append(f.slug, v));
              continue;
            }
          }

          params.append(f.slug, String(resolved));
        }
      }

      const endpoint = `/api/ngpep/${this.userId}/metabase-token?${params.toString()}`;
      const response = await apiClient.get<MetabaseTokenResponse>(endpoint, this.token!);
      const data = extractObjectPayload<MetabaseTokenResponse>(response.data);
      if (response.success && data) return data;
      console.error('❌ Failed to fetch Metabase token:', response.error);
      return null;
    } catch (error) {
      console.error('❌ Error fetching Metabase token:', error);
      return null;
    }
  }

  /** @deprecated Use backend-signed iframeUrl instead of building URLs on the frontend */
  buildMetabaseEmbedUrl(
    resourceType: 'question' | 'dashboard',
    token: string,
    baseUrl: string = 'https://analytics.hbox.ai'
  ): string {
    return `${baseUrl}/embed/${resourceType}/${token}#downloads=results`;
  }

  async getMetabaseQuestions(): Promise<MetabaseQuestion[]> {
    this.ensureContext();
    try {
      const endpoint = `/api/ngpep/${this.userId}/metabase/questions`;
      const response = await apiClient.get<MetabaseQuestion[]>(endpoint, this.token!);
      if (response.success && response.data) return extractArrayPayload<MetabaseQuestion>(response.data);
      console.error('❌ Failed to fetch Metabase questions:', response.error);
      return [];
    } catch (error) {
      console.error('❌ Error fetching Metabase questions:', error);
      return [];
    }
  }

  async createDashboardItem(data: {
    customName: string;
    metabaseQuestionId: number;
    metabaseQuestionName?: string;
  }): Promise<{ success: boolean; id?: number; error?: string }> {
    this.ensureContext();
    try {
      const endpoint = `/api/ngpep/${this.userId}/dashboard-items`;
      const response = await apiClient.post<{ success: boolean; id: number }>(endpoint, data, this.token!);
      if (response.success && response.data) return { success: true, id: response.data.id };
      return { success: false, error: response.error || 'Failed to create dashboard item' };
    } catch (error) {
      console.error('❌ Error creating dashboard item:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  async getDashboardItems(userType?: string): Promise<DashboardItem[]> {
    this.ensureContext();
    try {
      const query = userType ? `?userType=${encodeURIComponent(userType.toUpperCase())}` : '';
      const endpoint = `/api/ngpep/${this.userId}/dashboard-items${query}`;
      const response = await apiClient.get<DashboardItem[]>(endpoint, this.token!);
      if (response.success && response.data) return extractArrayPayload<DashboardItem>(response.data);
      console.error('❌ Failed to fetch dashboard items:', response.error);
      return [];
    } catch (error) {
      console.error('❌ Error fetching dashboard items:', error);
      return [];
    }
  }

  /**
   * Get signed iframe URL for the default dashboard assigned to a userType.
   *
   * Returns the full DefaultIframeResponse so callers can inspect success/message.
   * userType MUST be uppercase (ES, CS, PE, PS …).
   * userEmail is optional — backend resolves from loginUserId if absent.
   */
  async getDefaultDashboardIframe(
    userType: string,
    userEmail?: string
  ): Promise<DefaultIframeResponse | null> {
    this.ensureContext();
    try {
      const role = userType.toUpperCase();
      let endpoint = `/api/ngpep/${this.userId}/dashboard-items/default-iframe?userType=${encodeURIComponent(role)}`;
      if (userEmail) endpoint += `&user_email=${encodeURIComponent(userEmail)}`;

      const response = await apiClient.get<DefaultIframeResponse>(endpoint, this.token!);

      const data = extractObjectPayload<DefaultIframeResponse>(response.data);

      if (data) return data;

      console.error('❌ Failed to get default iframe URL:', response.error);
      return { success: false, message: response.error ?? 'Failed to get default iframe URL' };
    } catch (error) {
      console.error('❌ Error fetching default iframe URL:', error);
      return { success: false, message: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Get signed iframe URL for a saved dashboard item.
   * Returns full ItemIframeResponse so caller can inspect success/message.
   * userEmail is optional — backend resolves from loginUserId if absent.
   */
  async getDashboardItemIframeUrl(
    itemId: number,
    userEmail?: string,
    userType?: string
  ): Promise<ItemIframeResponse | null> {
    this.ensureContext();
    try {
      const query = new URLSearchParams();
      if (userEmail) query.set('user_email', userEmail);
      if (userType) query.set('userType', userType.toUpperCase());

      const base = `/api/ngpep/${this.userId}/dashboard-items/${itemId}/iframe`;
      const endpoint = query.toString() ? `${base}?${query.toString()}` : base;

      const response = await apiClient.get<ItemIframeResponse>(endpoint, this.token!);

      const data = extractObjectPayload<ItemIframeResponse>(response.data);

      if (data) return data;

      console.error('❌ Failed to get iframe URL:', response.error);
      return { success: false, message: response.error ?? 'Failed to get iframe URL' };
    } catch (error) {
      console.error('❌ Error fetching iframe URL:', error);
      return { success: false, message: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Fetch flat list of all Metabase resources (questions + dashboards) with typed composite keys.
   */
  async fetchMetabaseResourcesFlat(): Promise<MetabaseResourceFlat[]> {
    this.ensureContext();
    try {
      const endpoint = `/api/ngpep/${this.userId}/metabase/resources/flat`;
      const response = await apiClient.get<MetabaseResourceFlat[]>(endpoint, this.token!);
      if (response.success && response.data) return extractArrayPayload<MetabaseResourceFlat>(response.data);
      console.error('❌ Failed to fetch flat Metabase resources:', response.error);
      return [];
    } catch (error) {
      console.error('❌ Error fetching flat Metabase resources:', error);
      return [];
    }
  }
}

// Export singleton instance
export const enrollerAdminService = new EnrollerAdminService();
